import React, { useState, useRef } from 'react'; // <-- Import useRef
import useApi from '../../hooks/useApi';
import Layout from '../../components/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ProductBulkUpload = () => {
  const api = useApi();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null); // <-- Create a ref for the file input

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResponse(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a CSV file to upload.');
      return;
    }

    setLoading(true);
    setError('');
    setResponse(null);

    const formData = new FormData();
    formData.append('productFile', file);

    try {
      const res = await api.post('products/bulk-upload', formData);
      setResponse(res); // The whole response is useful here

      // Clear the form on success
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null; // Reset the file input element
      }

    } catch (err) {
      // The main message from the error object
      const mainMessage = err.message || 'An unexpected error occurred.';
      
      // The detailed error info, which might be a string like in your example
      const errorDetails = err.data?.data;

      // Combine them for a more informative message
      const fullError = errorDetails
        ? `${mainMessage} Details: ${errorDetails}`
        : mainMessage;
      
      setError(fullError);
      setResponse(err.data || null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Bulk Upload Products via CSV</h1>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="productFile" className="block text-sm font-medium text-gray-700 mb-2">
              CSV File
            </label>
            <input
              ref={fileInputRef} // <-- Attach the ref to the input
              type="file"
              id="productFile"
              name="productFile"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !file}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
          >
            {loading ? <LoadingSpinner isButton={true} /> : 'Upload and Process'}
          </button>
        </form>

        {error && <div className="mt-6 p-4 bg-red-100 text-red-700 rounded">{error}</div>}

        {response && (
          <div className="mt-6 p-4 border rounded-lg bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">{response.message}</h2>
            <div className="space-y-2">
              <p><span className="font-medium">Products Created:</span> <span className="text-green-600 font-bold">{response.data?.created || 0}</span></p>
              <p><span className="font-medium">Products Updated:</span> <span className="text-blue-600 font-bold">{response.data?.updated || 0}</span></p>
              
              {response.data?.errors && response.data.errors.length > 0 && (
                <div>
                  <h3 className="font-medium mt-4 mb-2 text-red-600">Errors Encountered:</h3>
                  <ul className="list-disc list-inside bg-red-50 p-3 rounded max-h-60 overflow-y-auto">
                    {response.data.errors.map((err, index) => (
                      <li key={index} className="text-sm text-red-800">
                        <strong>SKU:</strong> {err.sku || 'N/A'} - {err.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductBulkUpload;